import axios from 'axios';
import { refreshToken, getCurrentToken, clearAuthData } from './auth';
import { isPublicRoute } from '../utils/publicRoutes';
import { resetCrossTabActivity } from '../utils/crossTabActivity';

// Create axios instance with base configuration
const axiosInstance = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL,
    withCredentials: true,
    timeout: 10000, // 10 second timeout
});

// Track refresh token state to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
// Queue to hold failed requests while refreshing token
let failedQueue = [];
// Store pending requests to cancel them on logout
const pendingRequests = new Map();

/**
 * Process the queue of failed requests after token refresh
 * @param {Error|null} error - Error if refresh failed, null if successful
 * @param {string|null} token - New token if refresh successful, null if failed
 */
const processQueue = (error, token = null) => {
    failedQueue.forEach(({ resolve, reject, config }) => {
        if (error) {
            reject(error);
        } else {
            // Update the Authorization header with new token
            if (token) {
                config.headers.Authorization = `Bearer ${token}`;
            }
            resolve(config);
        }
    });

    failedQueue = [];
};

// Request interceptor to attach Authorization header
axiosInstance.interceptors.request.use(
    (config) => {
        // Get current token from localStorage
        const accessToken = getCurrentToken();

        // Check if token exists in localStorage
        if (!accessToken) {
            // BUG-74: public-route bypass — don't force-reload to '/' when the
            // page in scope is public (vendor-signup, vendor-portal, etc.).
            // Reject the request cleanly so the caller surfaces a graceful error,
            // and the user stays where they are.
            const onPublic = typeof window !== 'undefined' && isPublicRoute(window.location.pathname);
            if (!onPublic) {
                clearAuthData();
                window.location.href = '/';
            }
            return Promise.reject(new Error('No authentication token found'));
        }

        // Attach Authorization header if token exists
        if (accessToken) {
            config.headers.Authorization = `Bearer ${accessToken}`;
        }

        // Create cancel token for this request
        const source = axios.CancelToken.source();
        config.cancelToken = source.token;

        // Store the cancel function
        const requestId = `${config.method}-${config.url}-${Date.now()}`;
        pendingRequests.set(requestId, source);

        // Attach request ID to config for cleanup
        config.requestId = requestId;

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor to handle token refresh on 401 errors
axiosInstance.interceptors.response.use(
    (response) => {
        // Return successful responses unchanged
        // Reset activity on successful API calls
        resetCrossTabActivity();
        return response;
    },
    async (error) => {
        const originalRequest = error.config;

        // Handle 401 Unauthorized errors
        if (error.response?.status === 401 && !originalRequest._retry) {
            // Prevent infinite retry loops
            originalRequest._retry = true;

            // If already refreshing, queue this request
            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject, config: originalRequest });
                }).then((config) => {
                    return axiosInstance(config);
                });
            }

            // Start refresh process
            isRefreshing = true;

            try {
                // Use auth function to refresh token
                const refreshResult = await refreshToken();

                if (refreshResult.success) {
                    // Refresh successful - process queued requests with new token
                    processQueue(null, refreshResult.token);

                    // Retry original request with new token
                    originalRequest.headers.Authorization = `Bearer ${refreshResult.token}`;
                    return axiosInstance(originalRequest);
                } else {
                    // Refresh failed - clear auth data and reject all queued requests
                    clearAuthData();
                    processQueue(new Error(refreshResult.error || 'Token refresh failed'), null);
                    return Promise.reject(error);
                }

            } catch (refreshError) {
                // Refresh failed - clear auth data and clear queue
                console.error('Token refresh error:', refreshError);
                clearAuthData();
                processQueue(refreshError, null);
                return Promise.reject(error);
            } finally {
                isRefreshing = false;
            }
        }

        // Return other errors unchanged
        return Promise.reject(error);
    }
);

// Response interceptor to clean up completed requests
axiosInstance.interceptors.response.use(
    (response) => {
        // Remove from pending requests on success
        if (response.config.requestId) {
            pendingRequests.delete(response.config.requestId);
        }
        return response;
    },
    (error) => {
        // Remove from pending requests on error
        if (error.config?.requestId) {
            pendingRequests.delete(error.config.requestId);
        }
        return Promise.reject(error);
    }
);

/**
 * Cancel all pending requests (call this on logout)
 */
export const cancelAllRequests = () => {
    pendingRequests.forEach((source, requestId) => {
        source.cancel('Request cancelled due to logout');
        pendingRequests.delete(requestId);
    });
};

export default axiosInstance;

/* 
 * CONFIGURATION NOTES:
 * 
 * 1. API Endpoints:
 *    - Change refresh endpoint in auth.js refreshToken function
 *    - Update baseURL in axios instance above if needed
 * 
 * 2. Token Management:
 *    - Token is managed through localStorage via auth.js functions
 *    - Auth functions handle token validation and refresh logic
 * 
 * 3. Queue Logic:
 *    - failedQueue holds requests that fail due to 401 while refreshing
 *    - processQueue replays queued requests with new token or rejects them
 *    - isRefreshing prevents multiple simultaneous refresh attempts
 * 
 * 4. Dependencies:
 *    - Uses auth.js functions for token management
 *    - Logout is handled externally (e.g., from sidebar components)
 * 
 * 5. Usage:
 *    - Import and use this axios instance for all API calls
 *    - Automatic token refresh and request queueing on 401 errors
 */